// Lumina Voice dictation — thin wrappers over the FastAPI proxy (→ ehi.ariaone.ai).
// No local state; every call forwards the tenant JWT via the shared axios instance.
import { api } from "@/lib/api";

export const ensureApiKey = () => api.post("/api/dictation/ensure-api-key", {});
export const ensureNpi = (body) => api.post("/api/dictation/ensure-npi", body);
export const ensurePatient = (body) => api.post("/api/dictation/ensure-patient", body);

export async function quickContext(body) {
  const { data } = await api.post("/api/dictation/patient-context/quick", body);
  return data;
}
// Full EHR grounding — richer than the quick call (problems, meds, vitals, labs,
// allergies, coverage). Used to enrich the chart after the quick call lands.
export async function deepContext(body) {
  const { data } = await api.post("/api/dictation/patient-context", body);
  return data;
}
export async function engagementEvents(params) {
  const { data } = await api.get("/api/dictation/engagement-events", { params });
  return data;
}
export async function createSession(body) {
  const { data } = await api.post("/api/dictation/sessions", body);
  return data;
}
// #5: reuse a same-day session for this patient instead of always creating new.
export async function sameDaySession(params) {
  const { data } = await api.get("/api/dictation/sessions/same-day", { params });
  return data;
}
export async function continueSession(sessionId) {
  const { data } = await api.put(`/api/dictation/sessions/${sessionId}/continue`);
  return data;
}
export async function generateSoap(body) {
  const { data } = await api.post("/api/dictation/soap-note", body);
  return data;
}
export async function getSoap(soapId) {
  const { data } = await api.get(`/api/dictation/soap-note/${soapId}`);
  return data;
}
export async function updateSoap(soapId, body) {
  const { data } = await api.put(`/api/dictation/soap-note/${soapId}`, body || {});
  return data;
}
export async function regenerateSoap(soapId, body) {
  const { data } = await api.post(`/api/dictation/soap-note/${soapId}/regenerate`, body || {});
  return data;
}
export async function sendToClaim(sessionId, body) {
  const { data } = await api.post(`/api/dictation/sessions/${sessionId}/send-to-ehi-claim`, body);
  return data;
}
export async function saveSessionTranscript(sessionId, transcript) {
  const { data } = await api.put(`/api/dictation/sessions/${sessionId}`, { transcript, status: "completed" });
  return data;
}
export async function getNote(noteId, params) {
  const { data } = await api.get(`/api/edi/note-to-claim/notes/${noteId}`, params ? { params } : undefined);
  return data?.note || data;
}
// TOV — PATCH is TAG-ONLY (never rewrites the note); 400 → reject with the API's message.
export async function patchNoteTov(noteId, tov) {  try {
    const { data } = await api.patch(`/api/edi/note-to-claim/notes/${noteId}/tov`, { tov });
    return data?.note || data;
  } catch (e) {
    const d = e?.response?.data?.detail;
    throw new Error(typeof d === "string" ? d : "The API rejected that visit type.");
  }
}
// Edit the encounter note (full-replace of each field, >=3 chars). soap_text feeds coding;
// formatted_note is the provider's record of truth. 400 (too short / neither field) → reject.
export async function patchNoteSoap(noteId, body) {
  try {
    const { data } = await api.patch(`/api/edi/note-to-claim/notes/${noteId}/soap`, body);
    return data?.note || data;
  } catch (e) {
    const d = e?.response?.data?.detail;
    throw new Error(typeof d === "string" ? d : "The note did not save.");
  }
}
// Re-run the coding pipeline on the note (background job; poll getNote until completed/failed).
export async function reprocessNote(noteId) {
  const { data } = await api.post(`/api/edi/note-to-claim/notes/${noteId}/reprocess`, {});
  return data; // { status: "queued" }
}

// The client's visit types (GET /clients) + the active provider's per-TOV templates.
export async function loadTovContext(clientId) {
  const [clients, providers] = await Promise.all([
    api.get("/api/edi/note-to-claim/clients").then((r) => r.data?.clients || []).catch(() => []),
    listProviders().catch(() => []),
  ]);
  const c = clients.find((x) => String(x.client_id) === String(clientId));
  const p = providers.find((x) => x.status === "active") || providers[0];
  return {
    tov_list: Array.isArray(c?.tov_list) ? c.tov_list : [],
    tov_formats: p?.soap_profile?.tov_formats || {},
    tov_provider: p?.provider_name || "",
  };
}
export async function listProviders() {
  const { data } = await api.get("/api/edi/note-to-claim/providers");
  return Array.isArray(data?.providers) ? data.providers : [];
}

// The SOAP result shape is loosely typed upstream — normalize defensively to S/O/A/P.
export function normalizeSoap(result) {
  if (!result) return null;
  const r = result.soap && typeof result.soap === "object" ? result.soap : result;
  const pick = (...keys) => {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "string" && v.trim()) return v.trim();
      if (v && typeof v === "object") {
        // some shapes nest { text } / { content }
        if (typeof v.text === "string" && v.text.trim()) return v.text.trim();
        if (typeof v.content === "string" && v.content.trim()) return v.content.trim();
      }
    }
    return "";
  };
  const soap = {
    subjective: pick("subjective", "s", "Subjective", "S"),
    objective: pick("objective", "o", "Objective", "O"),
    assessment: pick("assessment", "a", "Assessment", "A"),
    plan: pick("plan", "p", "Plan", "P"),
    icd: Array.isArray(r.icd_codes) ? r.icd_codes : Array.isArray(r.icd) ? r.icd : Array.isArray(result.icd_codes) ? result.icd_codes : [],
    cpt: Array.isArray(r.cpt_codes) ? r.cpt_codes : Array.isArray(r.cpt) ? r.cpt : Array.isArray(result.cpt_codes) ? result.cpt_codes : [],
  };
  const hasSoap = soap.subjective || soap.objective || soap.assessment || soap.plan;
  if (!hasSoap) {
    // last resort: raw text blob
    const raw = pick("note", "text", "content", "soap_note");
    if (raw) soap.subjective = raw;
  }
  return soap;
}

// The Note-to-Claim engine writes the SOAP as a plain-text blob in
// encounter.soap_text (Chief Complaint / Subjective / Objective / Assessment /
// Plan). Parse it into sections for the SOAP layout. This is the path that
// actually works — the dictation soap-note endpoint needs a managed-LLM key
// the account may not have.
export function soapFromNote(note) {
  const enc = note?.encounter || {};
  let text = enc.soap_text || "";
  if (!text && enc.soap_note && typeof enc.soap_note === "object") {
    const s = enc.soap_note;
    return {
      chiefComplaint: s.chief_complaint || s.cc || "",
      subjective: s.subjective || "", objective: s.objective || "",
      assessment: s.assessment || "", plan: s.plan || "", raw: "",
    };
  }
  if (!text && typeof enc.soap_note === "string") text = enc.soap_note;
  text = text || "";
  const section = (label, stops) => {
    const stop = stops.length ? `(?=\\n\\s*(?:${stops.join("|")})\\s*:|$)` : "$";
    const m = text.match(new RegExp(`${label}\\s*:?\\s*([\\s\\S]*?)${stop}`, "i"));
    return m ? m[1].trim() : "";
  };
  const cc = section("Chief Complaint", ["Subjective", "Objective", "Assessment", "Plan"]);
  const subj = section("Subjective", ["Objective", "Assessment", "Plan"]);
  const obj = section("Objective", ["Assessment", "Plan"]);
  const assess = section("Assessment", ["Plan"]);
  const plan = section("Plan", []);
  const any = cc || subj || obj || assess || plan;
  return { chiefComplaint: cc, subjective: subj, objective: obj, assessment: assess, plan, raw: any ? "" : text };
}

export function codesFromNote(note) {
  return {
    icd: Array.isArray(note?.icd_codes) ? note.icd_codes : [],
    cpt: Array.isArray(note?.cpt_codes) ? note.cpt_codes : [],
    visitId: note?.visit_id || "",
  };
}

// Live insights: feed a (growing) transcript to analyze-segment and get back
// key symptoms, clinical observations, flags, mood and a running summary.
// This endpoint works on accounts where the soap-note LLM key is absent.
export async function analyzeSegment(transcript, previousContext = "") {
  const { data } = await api.post("/api/dictation/analyze-segment", {
    transcript, previous_context: previousContext || undefined,
  });
  return data;
}

export function normalizeInsights(d) {
  if (!d || typeof d !== "object") return null;
  const flags = (Array.isArray(d.clinical_flags) ? d.clinical_flags : []).map((f) =>
    typeof f === "string"
      ? { message: f, severity: "info" }
      : { message: f.message || f.label || f.text || f.flag || JSON.stringify(f), severity: (f.severity || f.level || f.priority || "info") }
  );
  return {
    symptoms: Array.isArray(d.key_symptoms) ? d.key_symptoms : [],
    observations: [...(d.problems_observations || []), ...(d.provider_observations || [])],
    flags,
    summary: d.summary || "",
    mood: d.mood?.primary || "",
  };
}

// The dictation soap-note endpoint returns a structured draft:
// result.soap_note = { subjective, objective, assessment, plan } and a
// readable_soap_note text blob. Prefer the structured fields; fall back to
// parsing the readable blob per-section so S/O/A/P always render.
function parseReadable(text, label, stops) {
  if (!text) return "";
  const stop = stops.length ? `(?=\\n\\s*(?:${stops.join("|")})\\s*:|$)` : "$";
  const m = text.match(new RegExp(`\\n?\\s*${label}\\s*:?\\s*([\\s\\S]*?)${stop}`, "i"));
  return m ? m[1].trim() : "";
}
export function soapFromDictation(result) {
  if (!result) return null;
  const sn = result.soap_note || {};
  const readable = typeof result.readable_soap_note === "string" ? result.readable_soap_note : "";
  const pick = (k, label, stops) => (sn[k] && String(sn[k]).trim()) || parseReadable(readable, label, stops);
  const s = {
    chiefComplaint: "",
    subjective: pick("subjective", "Subjective", ["Objective", "Assessment", "Plan"]),
    objective: pick("objective", "Objective", ["Assessment", "Plan"]),
    assessment: pick("assessment", "Assessment", ["Plan"]),
    plan: pick("plan", "Plan", []),
  };
  const any = s.subjective || s.objective || s.assessment || s.plan;
  return { ...s, raw: any ? "" : readable };
}

export function codesFromDictation(result) {
  const dx = Array.isArray(result?.diagnoses) ? result.diagnoses : [];
  const icd = dx
    .map((d) => (typeof d === "string"
      ? { code: "", description: d }
      : { code: d.code || d.icd10 || d.icd || "", description: d.description || d.name || d.title || "" }))
    .filter((x) => x.code || x.description);
  return { icd, cpt: [], visitId: "" };
}

// Care-management checklist: persist done/pending so items fold into the claim.
export async function patchEngagement(eventId, patientKey, status) {
  const { data } = await api.patch(`/api/dictation/engagement-events/${eventId}`, { patient_key: patientKey, status });
  return data;
}

// Re-prescribe: expiring meds → stage into the claim's e-Rx queue.
export async function represcribeAlerts(patientKey) {
  const { data } = await api.get("/api/dictation/represcribe-alerts", { params: { patient_key: patientKey } });
  return data;
}
export async function stageReprescribe(patientKey, med) {
  const { data } = await api.post("/api/dictation/represcribe", { patient_key: patientKey, med, staged: true });
  return data;
}
